import { css, ListItemText, styled, Theme } from "@mui/material";

import { getColor } from "@hooks";
import LogoDark from "@public/about_us/bracket_dark_mode.svg";
import LogoLight from "@public/about_us/bracket_light_mode.svg";
import { cssForHeaderWithLinearGradient, cssPropertiesToString } from "@utils";

const verticalLabelWidth = 80;

export const AreasContainer = styled("div")(
  ({ theme }) => css`
    position: relative;
    overflow: hidden;

    border: 1px solid transparent;
    border-radius: 4px;

    ${theme.breakpoints.up("mobile")} {
      margin: 24px 16px 48px 16px;
    }

    ${theme.breakpoints.up("tablet")} {
      margin: 24px 64px 24px 64px;
    }

    ${theme.breakpoints.up("desktop")} {
      margin: 24px 110px 24px 110px;

      height: 427px;
      & > div {
        width: calc(100% - ${verticalLabelWidth}px * 3);
      }
    }

    ${theme.breakpoints.up("desktopXl")} {
      margin: 24px 110px 24px 110px;
    }

    .MuiListItemButton-root {
      &.Mui-disabled {
        opacity: 1;
      }
    }
  `,
);

interface HorizontalAccordionOuterWrapperProps {
  areaIndex: number;
  openedIndex: number;
  areasAmount: number;
}

export const HorizontalAccordionOuterWrapper = styled("div", {
  shouldForwardProp: prop =>
    !["areaIndex", "openedIndex", "areasAmount"].includes(prop.toString()),
})<HorizontalAccordionOuterWrapperProps>(
  ({ areaIndex, openedIndex, areasAmount, theme }) => css`
    height: 100%;
    background: ${theme.palette.background.default};

    position: absolute;
    ${areaIndex <= openedIndex
      ? css`
          left: calc(${areaIndex} * ${verticalLabelWidth}px);
        `
      : css`
          left: calc(
            100% - ${areasAmount - areaIndex} * ${verticalLabelWidth}px
          );
        `}

    transition: left 0.6s;

    .MuiListItemButton-root {
      align-items: end;
      min-width: ${verticalLabelWidth}px;
    }
  `,
);

interface HorizontalAccordionInnerWrapperProps {
  color: string;
  borderColor?: string;
}

export const HorizontalAccordionInnerWrapper = styled("div", {
  shouldForwardProp: prop => prop !== "color" && prop !== "borderColor",
})<HorizontalAccordionInnerWrapperProps>(
  ({ color, borderColor }) => css`
    display: flex;
    height: 100%;
    background: ${color};

    ${borderColor &&
    css`
      border-left: 1px dashed ${borderColor};
    `}
  `,
);

interface VerticalAccordionWrapperProps {
  areaIndex: number;
  openedIndex: number;
  color: string;
  borderColor?: string;
}

export const VerticalAccordionWrapper = styled("div", {
  shouldForwardProp: prop =>
    !["areaIndex", "openedIndex", "color", "borderColor"].includes(
      prop.toString(),
    ),
})<VerticalAccordionWrapperProps>(
  ({ areaIndex, openedIndex, color, borderColor, theme }) => css`
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    overflow: hidden;
    background: ${color};

    transition: height 0.6s;

    ${borderColor &&
    css`
      border-top: 1px dashed ${borderColor};
    `}

    ${theme.breakpoints.up("mobile")} {
      height: ${areaIndex === openedIndex ? "180px" : "48px"};
    }

    ${theme.breakpoints.up("tablet")} {
      height: ${areaIndex === openedIndex ? "220px" : "72px"};
    }

    .MuiListItemButton-root {
      ${theme.breakpoints.up("mobile")} {
        min-height: 48px;
        max-height: 48px;

        padding: 0 0 0 16px;
      }

      ${theme.breakpoints.up("tablet")} {
        min-height: 72px;
        max-height: 72px;

        padding: 0 0 0 38px;
      }
    }
  `,
);

interface WorkingAreaLabelProps {
  isOpened: boolean;
}

export const WorkingAreaLabel = styled(ListItemText, {
  shouldForwardProp: prop => prop !== "isOpened",
})<WorkingAreaLabelProps>(
  ({ theme, isOpened }) => css`
    ${cssForHeaderWithLinearGradient(theme)}

    ${theme.breakpoints.up("desktop")} {
      writing-mode: vertical-rl;
      transform: rotate(180deg);
    }

    .MuiTypography-root {
      ${theme.breakpoints.up("mobile")} {
        ${cssPropertiesToString(theme.typography.heading_h6_bold_mobile)}
      }

      ${theme.breakpoints.up("tablet")} {
        ${cssPropertiesToString(theme.typography.heading_h3_bold_desktop)}
      }

      ${theme.breakpoints.up("desktopXl")} {
        ${cssPropertiesToString(theme.typography.display_bold_small)}
      }

      transition: font-size 0.6s;
    }

    ${theme.breakpoints.between("desktop", "desktopXl")} {
      ${isOpened &&
      css`
        .MuiTypography-root {
          font-size: 0px;
          transition: font-size 0.6s;
        }
      `}
    }

    transition: opacity 0.6s;
    ${theme.breakpoints.up("desktopXl")} {
      ${isOpened &&
      css`
        opacity: 0;
        transition: opacity 0.6s;
      `}
    }
  `,
);

export const WorkingAreaInfo = styled("div")(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 8px;

    ${theme.breakpoints.up("mobile")} {
      padding: 0px 8px 32px 16px;
    }

    ${theme.breakpoints.up("tablet")} {
      padding: 0px 24px 24px 38px;
    }

    ${theme.breakpoints.up("desktop")} {
      align-self: flex-end;
      padding: 40px 32px 32px 4px;
    }

    ${theme.breakpoints.up("desktopXl")} {
      padding: 40px 125px 32px 4px;
    }
  `,
);

export const WorkingAreaTitle = styled("p")(
  ({ theme }) => css`
    ${cssForHeaderWithLinearGradient(theme)}

    ${theme.breakpoints.up("mobile")} {
      ${cssPropertiesToString(theme.typography.heading_h6_bold_mobile)}
    }

    ${theme.breakpoints.up("tablet")} {
      ${cssPropertiesToString(theme.typography.heading_h3_bold_tablet)}
    }

    ${theme.breakpoints.up("desktop")} {
      ${cssPropertiesToString(theme.typography.heading_h3_bold_desktop)}
    }

    ${theme.breakpoints.up("desktopXl")} {
      ${cssPropertiesToString(theme.typography.display_bold_large)}
    }
  `,
);

export const WorkingAreaDescription = styled("p")(
  ({ theme }) => css`
    color: ${getColor(c => c.tokens.text["sub-600"], theme)};

    ${theme.breakpoints.up("mobile")} {
      ${cssPropertiesToString(theme.typography.paragraph_small_regular)}
    }

    ${theme.breakpoints.up("desktop")} {
      ${cssPropertiesToString(theme.typography.paragraph_large_regular)}
    }
  `,
);

export const BracketCss = (theme: Theme) => css`
  position: absolute;

  ${theme.breakpoints.up("mobile")} {
    scale: 0.45;
    top: -34px;
    left: -10px;
  }
  ${theme.breakpoints.up("tablet")} {
    scale: 0.62;
    top: -22px;
    left: 16px;
  }
  ${theme.breakpoints.up("desktop")} {
    scale: 1;
    top: 0;
    left: 0;

    z-index: 10;
  }
`;

export const BracketDark = styled(LogoDark)(
  ({ theme }) => css`
    ${BracketCss(theme)}
  `,
);

export const BracketLight = styled(LogoLight)(
  ({ theme }) => css`
    ${BracketCss(theme)}
  `,
);
